import { Injectable, NgZone } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { AuthService } from '../../services/auth/auth.service';
import { tap, catchError, finalize } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { Auth } from './auth.actions';
import {
  IAuthResponse,
  IUser,
  ILoginRequest,
} from '../../interfaces/auth.interface';
import { IApiResponse } from '../../interfaces/api-response.interface';
import { Router } from '@angular/router';

export interface AuthStateModel {
  token: string | null;
  user: IUser | null;
  loading: boolean;
  error: any | null;
  forgotPassword: boolean;
  resetPassword: boolean;
  logout: boolean;
  changePassword: boolean;
  credentials: ILoginRequest | null;
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    token: null,
    user: null,
    loading: false,
    error: null,
    forgotPassword: false,
    resetPassword: false,
    logout: false,
    changePassword: false,
    credentials: null,
  },
})
@Injectable()
export class AuthState {
  constructor(
    private authService: AuthService,
    private router: Router,
    private ngZone: NgZone
  ) {}

  @Selector()
  static getToken(state: AuthStateModel): string | null {
    return state.token;
  }

  @Selector()
  static getUser(state: AuthStateModel): IUser | null {
    return state.user;
  }

  @Selector()
  static getLoading(state: AuthStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static getError(state: AuthStateModel): any | null {
    return state.error;
  }

  @Selector()
  static getForgotPassword(state: AuthStateModel): boolean {
    return state.forgotPassword;
  }

  @Selector()
  static getResetPassword(state: AuthStateModel): boolean {
    return state.resetPassword;
  }

  @Selector()
  static getLogout(state: AuthStateModel): boolean {
    return state.logout;
  }

  @Selector()
  static getChangePassword(state: AuthStateModel): boolean {
    return state.changePassword;
  }

  @Selector()
  static getCredentials(state: AuthStateModel): ILoginRequest | null {
    return state.credentials;
  }

  @Selector()
  static getPermissions(state: AuthStateModel): string[] {
    return (state.user?.role?.permissions || []).map(p => p.trim()).filter(Boolean);
  }

  @Action(Auth.Login)
  login(ctx: StateContext<AuthStateModel>, action: Auth.Login) {
    ctx.patchState({ loading: true, error: null });

    if (action.payload.remember) {
      ctx.patchState({ credentials: action.payload });
    } else {
      ctx.patchState({ credentials: null });
    }

    return this.authService.login(action.payload).pipe(
      tap((response: IApiResponse<IAuthResponse>) => {
        ctx.dispatch(new Auth.LoginSuccess(response.data));
      }),
      catchError((error) => {
        ctx.dispatch(new Auth.LoginFailure(error));
        return throwError(() => error);
      })
    );
  }

  @Action(Auth.ForgotPassword)
  forgotPassword(
    ctx: StateContext<AuthStateModel>,
    action: Auth.ForgotPassword
  ) {
    ctx.patchState({ loading: true, error: null });
    return this.authService.forgotPassword(action.payload).pipe(
      tap(() => {
        ctx.patchState({
          forgotPassword: true,
          loading: false,
          error: null,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: false,
          error:
            error.error?.message ||
            'Error al solicitar el restablecimiento de contraseña',
        });
        return throwError(() => error);
      })
    );
  }

  @Action(Auth.ForgotPasswordReset)
  forgotPasswordReset(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ forgotPassword: false, loading: false, error: null });
  }

  @Action(Auth.ResetPassword)
  resetPassword(ctx: StateContext<AuthStateModel>, action: Auth.ResetPassword) {
    ctx.patchState({ loading: true, error: null });
    return this.authService.resetPassword(action.payload).pipe(
      tap(() => {
        ctx.patchState({
          resetPassword: true,
          loading: false,
          error: null,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: false,
          error: error.error?.message || 'Error al restablecer la contraseña',
        });

        if (error?.error?.statusCode === 404) {
          this.ngZone.run(() => {
            this.router.navigate(['/auth/link-expired']);
          });
        }

        return throwError(() => error);
      })
    );
  }

  @Action(Auth.ResetPasswordClear)
  resetPasswordClear(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ resetPassword: false, loading: false, error: null });
  }

  @Action(Auth.LoginSuccess)
  loginSuccess(ctx: StateContext<AuthStateModel>, action: Auth.LoginSuccess) {
    const token = action.payload.access_token;
    const user = action.payload.user;

    ctx.patchState({
      token: token,
      user: user,
      loading: false,
      error: null,
    });
  }

  @Action(Auth.LoginFailure)
  loginFailure(ctx: StateContext<AuthStateModel>, action: Auth.LoginFailure) {
    ctx.patchState({
      token: null,
      user: null,
      loading: false,
      error: action.error.error.message,
    });
  }

  @Action(Auth.Logout)
  logout(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ logout: true });
    return this.authService.logout().pipe(
      tap(() => {
        ctx.dispatch(new Auth.LogoutSuccess());
      }),
      catchError(() => {
        // Si el token ya era inválido el endpoint devuelve 401.
        // Igual limpiamos el estado local y redirigimos.
        ctx.dispatch(new Auth.LogoutSuccess());
        return of(null);
      }),
      finalize(() => {
        this.ngZone.run(() => {
          this.router.navigate(['/auth/login']);
        });
      })
    );
  }

  @Action(Auth.LogoutTransportista)
  logoutTransportista(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ logout: true });
    return this.authService.logout().pipe(
      tap(() => {
        ctx.dispatch(new Auth.LogoutSuccess());
        return of(true);
      }),
      catchError((error) => {
        ctx.patchState({ logout: false });
        return throwError(() => error);
      })
    );
  }

  @Action(Auth.ChangePassword)
  changePassword(
    ctx: StateContext<AuthStateModel>,
    action: Auth.ChangePassword
  ) {
    ctx.patchState({ loading: true });
    return this.authService.changePassword(action.payload).pipe(
      tap((response: IApiResponse<IAuthResponse>) => {
        ctx.patchState({
          changePassword: true,
          loading: false,
          error: null,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: false,
          error: error.error?.message || 'Error al cambiar la contraseña',
        });
        return throwError(() => error);
      })
    );
  }

  @Action(Auth.LogoutSuccess)
  logoutSuccess(ctx: StateContext<AuthStateModel>) {
    const current = ctx.getState();
    ctx.setState({
      token: null,
      user: null,
      loading: false,
      error: null,
      forgotPassword: false,
      resetPassword: false,
      logout: false,
      changePassword: false,
      credentials: current.credentials
    });
  }

  @Action(Auth.SetUser)
  setUser(ctx: StateContext<AuthStateModel>, action: Auth.SetUser) {
    ctx.patchState({ user: action.user });
  }

  @Action(Auth.ChangePasswordClear)
  changePasswordClear(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ changePassword: false, loading: false, error: null });
  }
}
